import type { Language } from './language';

export const COPY: Record<
  Language,
  {
    seo: { title: string; description: string };
    nav: {
      company: string;
      howItWorks: string;
      modules: string;
      useCases: string;
      security: string;
      faq: string;
      dashboard: string;
      requestDemo: string;
      languageLabel: string;
    };
    hero: {
      headline: string;
      subhead: string;
      primaryCta: string;
      secondaryCta: string;
      badge: string;
      screenshotAlt: string;
    };
	    howItWorks: {
	      title: string;
	      subtitle: string;
	      steps: Array<{ title: string; description: string }>;
	    };
	    connectors: {
	      title: string;
	      subtitle: string;
	      bullets: string[];
	      capabilityTitle: string;
	      capabilities: Array<{ title: string; description: string }>;
	      beforeLabel: string;
	      beforeValue: string;
	      beforeDescription: string;
	      afterLabel: string;
	      afterValue: string;
	      afterDescription: string;
	      sourcesTitle: string;
	      sources: string[];
	      outputs: string[];
	    };
	    channels: {
	      title: string;
	      subtitle: string;
	      bullets: string[];
	      methodsTitle: string;
	      methods: string[];
	      note: string;
	      exampleTitle: string;
	      exampleYou: string;
	      exampleQuestion: string;
	      exampleAnswer: string;
	      exampleFooter: string;
	    };
	    modules: {
	      title: string;
	      subtitle: string;
	      screenshotAlt: string;
	      items: Array<{ title: string; description: string }>;
    };
    useCases: {
      title: string;
      subtitle: string;
      highlights: Array<{
        title: string;
        label: string;
        description: string;
        tags: string[];
        imageAlt: string;
      }>;
      moreTitle: string;
      moreItems: string[];
    };
    demoStory: {
      title: string;
      subtitle: string;
      promptLabel: string;
      prompt: string;
      screenshotAlt: string;
    };
    security: {
      title: string;
      subtitle: string;
      bullets: string[];
    };
    faq: {
      title: string;
      subtitle: string;
      items: Array<{ q: string; a: string }>;
    };
    demo: {
      title: string;
      subtitle: string;
      contactTitle: string;
      contactBody: string;
      form: {
        fullName: string;
        workEmail: string;
        company: string;
        role: string;
        interest: string;
        message: string;
        consent: string;
        submit: string;
        submitting: string;
        successTitle: string;
        successBody: string;
        errorTitle: string;
        errorBody: string;
        retry: string;
        emailUs: string;
        endpointMissingTitle: string;
        endpointMissingBody: string;
        validation: {
          required: string;
          invalidEmail: string;
          consentRequired: string;
        };
        interestOptions: {
          dashboard: string;
          templates: string;
          documents: string;
          map: string;
          notSure: string;
        };
      };
    };
    cookie: {
      message: string;
      learnMore: string;
      accept: string;
      close: string;
    };
    footer: {
      tagline: string;
      emailLabel: string;
      location: string;
      legal: string;
      company: string;
      privacy: string;
      terms: string;
      copyright: string;
    };
  }
> = {
  en: {
    seo: {
      title: 'ERICA — AI for your data | Rekayasa',
      description:
        'Ask in plain language. ERICA turns your spreadsheets, reports, and databases into dashboards, insights, and maps in seconds.',
    },
    nav: {
      company: 'About Company',
      howItWorks: 'How it works',
      modules: 'Modules',
      useCases: 'Use cases',
      security: 'Security',
      faq: 'FAQ',
      dashboard: 'ERICA Dashboard',
      requestDemo: 'Request demo',
      languageLabel: 'Language',
    },
	    hero: {
	      badge: 'Ask data. Find insights.',
	      headline: 'Natural-language analytics for business teams.',
	      subhead:
	        'From raw data to insight in seconds. Business users can explore data without waiting on analyst or engineering queues.',
      primaryCta: 'Request demo',
      secondaryCta: 'See how it works',
      screenshotAlt: 'ERICA dashboard screenshot',
    },
	    howItWorks: {
	      title: 'Add data, ask questions, get results',
	      subtitle: 'A user journey built for non-technical business users.',
	      steps: [
	        {
	          title: 'Connect all your data sources',
	          description: 'Bring spreadsheets, reports, and databases your team already uses.',
	        },
	        {
	          title: 'Ask for analysis',
	          description: 'Write questions in natural language and ERICA handles the analysis flow.',
	        },
	        {
	          title: 'Get results instantly',
	          description: 'Receive dashboards, summaries, and priority insights you can act on now.',
	        },
	      ],
	    },
	    connectors: {
	      title: 'Data foundation for business decisions',
	      subtitle:
	        'One platform, many data sources. ERICA acts as an analysis layer across your structured and unstructured data.',
	      bullets: [
	        'Connect to your existing files, databases, APIs, and reports.',
	        'No heavy ETL project just to start asking business questions.',
	        'Integrations can follow your environment, governance, and access controls.',
	      ],
	      capabilityTitle: 'Why this matters',
	      capabilities: [
	        {
	          title: 'Reduce technical bottlenecks',
	          description: 'Business teams can run analysis without waiting on data engineering handoffs.',
	        },
	        {
	          title: 'Empower non-technical users',
	          description: 'Ask naturally and get analytics output without SQL-first workflows.',
	        },
	        {
	          title: 'Trustworthy outputs',
	          description: 'Insights stay tied to source context so teams can validate with confidence.',
	        },
	      ],
	      beforeLabel: 'Traditional flow',
	      beforeValue: '5 days',
	      beforeDescription: 'Typical cycle to gather data, prepare analysis, and present a dashboard.',
	      afterLabel: 'With ERICA',
	      afterValue: '< 5 minutes',
	      afterDescription: 'From question to first decision-ready insight and dashboard view.',
	      sourcesTitle: 'Common sources',
	      sources: ['CSV / XLSX', 'SQL databases', 'APIs', 'Data warehouse', 'PDF reports', 'Internal systems'],
	      outputs: ['Dashboards', 'Insights', 'Maps'],
	    },
	    channels: {
	      title: 'Ask and access — anywhere',
	      subtitle:
	        'Don’t force your team into a new workflow. Use ERICA on the web and integrate it into the channels your team already uses.',
	      bullets: [
	        'Ask from WhatsApp or Telegram (via integration), or use the web app.',
	        'Get concise answers and a dashboard view to dive deeper.',
	        'Bring insights to decision-makers where they already work.',
	      ],
	      methodsTitle: 'Access options',
	      methods: ['Web app', 'Mobile friendly', 'WhatsApp (integration)', 'Telegram (integration)', 'Custom integration'],
	      note: 'Integrations available by request',
	      exampleTitle: 'Example',
	      exampleYou: 'You',
	      exampleQuestion: 'Show the top 3 issues from last week’s conversations and prepare a dashboard.',
	      exampleAnswer:
	        'Here’s a summary of the top issues, what changed week‑to‑week, and what to prioritize next. I also prepared a dashboard view for a deeper drill‑down.',
	      exampleFooter: 'Dashboard link (internal) • Key insights • Suggested next steps',
	    },
	    modules: {
	      title: 'One platform. Multiple ways to explore.',
	      subtitle:
	        'Dashboards, templates, documents, and maps—built for decision-makers.',
      screenshotAlt: 'ERICA platform screenshot',
      items: [
        {
          title: 'Dashboards on the fly',
          description:
            'Turn questions into charts, metrics, and interactive dashboards.',
        },
        {
          title: 'Use-case templates',
          description: 'Start from proven templates and adapt to your context.',
        },
        {
          title: 'Ask your documents',
          description:
            'Search and summarize reports and attachments with confidence.',
        },
        {
          title: 'Spatial insights',
          description: 'Understand what’s happening on the map, not just in tables.',
        },
      ],
    },
    useCases: {
      title: 'Use-case templates for many businesses',
      subtitle:
        'From procurement to finance, HR, operations, and more—give ERICA your data and get actionable insights.',
      highlights: [
        {
          title: 'Hotel chatbot analytics',
          label: 'Hospitality',
          description:
            'Understand guest intent, top issues, and conversation outcomes from every chat session. ERICA highlights recurring friction points and service opportunities so teams can improve response quality faster.',
          tags: ['Chat analytics', 'Trends', 'Insights'],
          imageAlt: 'ERICA analyzing hotel chatbot data',
        },
        {
          title: 'Public safety & accident insights',
          label: 'Government',
          description:
            'Turn public datasets such as accident reports into clear dashboards for field and policy teams. ERICA helps surface hotspots, trend shifts, and priority signals so decisions are made with stronger evidence.',
          tags: ['Public data', 'Dashboards', 'Decision support'],
          imageAlt: 'ERICA dashboard for public accident data',
        },
        {
          title: 'Vendor & procurement intelligence',
          label: 'Procurement',
          description:
            'Explore vendor performance, survey sentiment, and procurement KPIs in one workflow. ERICA connects scattered tables and documents into structured insights that support faster reviews and action plans.',
          tags: ['Vendor', 'Survey', 'KPIs'],
          imageAlt: 'ERICA analyzing vendor and procurement data',
        },
      ],
      moreTitle: 'More template ideas',
      moreItems: [
        'Rencana Pengadaan Tahunan (Procurement Plan Report)',
        'Perencanaan/Realisasi Pengadaan (Plan vs Realisasi)',
        'Survey Kepuasan Vendor',
        'Keikutsertaan Vendor dalam Proses Pengadaan',
        'Direct Cost Saving',
        'Across Years Encumbrance',
        'SLA Pengadaan',
        'Summary FPBJ–SPK',
        'Statistik Pendaftaran Vendor',
        'Status Vendor (Blacklist / Verified / Watchlist)',
      ],
    },
    demoStory: {
      title: 'Intent → dashboard',
      subtitle:
        'Ask for what you need. ERICA produces dashboards that help leaders take quick action.',
      promptLabel: 'Example prompt',
      prompt: 'Create a vendor satisfaction survey dashboard from the attached file.',
      screenshotAlt: 'Chat and dashboard workflow screenshot',
    },
    security: {
      title: 'Security & governance first',
      subtitle:
        'Built to align with enterprise governance, internal security review, and compliance needs.',
      bullets: [
        'Designed to support alignment with Indonesia’s PDP Law (UU PDP) and data governance requirements',
        'We work with your security/DPO team on access controls, data handling, and review artifacts',
        'Deployment approach can be agreed to fit your constraints (cloud/on‑prem, by agreement)',
      ],
    },
    faq: {
      title: 'FAQ',
      subtitle: 'Quick answers for teams evaluating ERICA.',
      items: [
        {
          q: 'What data can ERICA work with?',
          a: 'Common business files and sources. In a demo, we’ll align ERICA to your actual data landscape.',
        },
        {
          q: 'Does ERICA support Bahasa Indonesia?',
          a: 'Yes—ERICA is built to support Bahasa Indonesia and English for business workflows.',
        },
        {
          q: 'How long does it take to get started?',
          a: 'It depends on your data and access requirements. We start with a demo, then map the quickest path to value.',
        },
        {
          q: 'Can we customize dashboards/templates?',
          a: 'Yes—templates and dashboards can be adapted to your KPIs and reporting style.',
        },
        {
          q: 'Where is my data stored?',
          a: 'We’ll discuss deployment options during security review to match your policies and constraints.',
        },
        {
          q: 'How do we request a demo?',
          a: 'Fill the form below and we’ll reach out to schedule a session.',
        },
      ],
    },
    demo: {
      title: 'Request a demo',
      subtitle:
        'Tell us what you want to explore—dashboards, templates, documents, or maps.',
      contactTitle: 'Prefer email?',
      contactBody: 'Reach us at sales@rekayasa.io and we’ll respond soon.',
      form: {
        fullName: 'Full name',
        workEmail: 'Work email',
        company: 'Company',
        role: 'Role / Function (optional)',
        interest: 'What do you want to explore?',
        message: 'Message (optional)',
        consent: 'I agree to be contacted about ERICA.',
        submit: 'Submit request',
        submitting: 'Submitting…',
        successTitle: 'Request received',
        successBody: "Thanks—we’ll reach out soon.",
        errorTitle: 'Something went wrong',
        errorBody:
          'Please try again. If the problem persists, email us directly.',
        retry: 'Try again',
        emailUs: 'Email us',
        endpointMissingTitle: 'Demo form not configured',
        endpointMissingBody:
          'This environment is missing a form endpoint. Please email us to request a demo.',
        validation: {
          required: 'This field is required.',
          invalidEmail: 'Please enter a valid work email.',
          consentRequired: 'Please confirm you agree to be contacted.',
        },
        interestOptions: {
          dashboard: 'Dashboard',
          templates: 'Templates',
          documents: 'Document Assistant',
          map: 'Map',
          notSure: 'Not sure yet',
        },
      },
    },
    cookie: {
      message:
        'We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.',
      learnMore: 'Learn more',
      accept: 'Accept',
      close: 'Close',
    },
    footer: {
      tagline:
        'ERICA helps business teams turn data into dashboards, insights, and actions.',
      emailLabel: 'Email',
      location: 'Jakarta, Indonesia',
      legal: 'Legal',
      company: 'About Company',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      copyright: 'Rekayasa Analisa Digital. All rights reserved.',
    },
  },
  id: {
    seo: {
      title: 'ERICA — AI untuk data Anda | Rekayasa',
      description:
        'Tanya dengan bahasa natural. ERICA mengubah spreadsheet, laporan, dan database menjadi dashboard, insight, dan peta dalam hitungan detik.',
    },
    nav: {
      company: 'Tentang Perusahaan',
      howItWorks: 'Cara kerja',
      modules: 'Modul',
      useCases: 'Use case',
      security: 'Keamanan',
      faq: 'FAQ',
      dashboard: 'ERICA Dashboard',
      requestDemo: 'Minta demo',
      languageLabel: 'Bahasa',
    },
	    hero: {
	      badge: 'Tanya data. Temukan insight.',
	      headline: 'Analytics berbasis bahasa natural.',
	      subhead:
	        'Dari data mentah ke insight dalam hitungan detik. Tim bisnis bisa analisa tanpa selalu bergantung pada data analyst atau engineer.',
      primaryCta: 'Minta demo',
      secondaryCta: 'Lihat cara kerja',
      screenshotAlt: 'Tangkapan layar dashboard ERICA',
    },
	    howItWorks: {
	      title: 'Tambah data, tanya, dapatkan hasil',
	      subtitle: 'User journey yang dibuat untuk pengguna bisnis non-teknis.',
	      steps: [
	        {
	          title: 'Hubungkan semua sumber data',
	          description: 'Gunakan spreadsheet, laporan, dan database yang tim Anda sudah pakai.',
	        },
	        {
	          title: 'Tanya kebutuhan analisis',
	          description: 'Tulis pertanyaan dengan bahasa natural, ERICA yang mengerjakan alurnya.',
	        },
	        {
	          title: 'Dapatkan hasil seketika',
	          description: 'Terima dashboard, ringkasan, dan insight prioritas untuk langsung ditindaklanjuti.',
	        },
	      ],
	    },
	    connectors: {
	      title: 'Fondasi data untuk keputusan bisnis',
	      subtitle:
	        'Satu platform, berbagai sumber data. ERICA menjadi lapisan analisis untuk data terstruktur dan tidak terstruktur.',
	      bullets: [
	        'Hubungkan file, database, API, dan laporan yang sudah Anda miliki.',
	        'Tidak perlu proyek ETL besar hanya untuk mulai menjawab pertanyaan bisnis.',
	        'Integrasi bisa mengikuti environment, governance, dan kontrol akses Anda.',
	      ],
	      capabilityTitle: 'Kenapa ini penting',
	      capabilities: [
	        {
	          title: 'Kurangi bottleneck teknis',
	          description: 'Tim bisnis bisa analisa tanpa menunggu handoff data engineering.',
	        },
	        {
	          title: 'Business user lebih mandiri',
	          description: 'Cukup tanya dengan bahasa natural tanpa workflow yang selalu SQL-first.',
	        },
	        {
	          title: 'Hasil lebih dapat dipercaya',
	          description: 'Insight tetap terhubung ke konteks sumber data untuk verifikasi cepat.',
	        },
	      ],
	      beforeLabel: 'Alur tradisional',
	      beforeValue: '5 hari',
	      beforeDescription: 'Siklus umum untuk kumpulkan data, siapkan analisis, dan susun dashboard.',
	      afterLabel: 'Dengan ERICA',
	      afterValue: '< 5 menit',
	      afterDescription: 'Dari pertanyaan ke insight awal dan dashboard yang siap dipakai.',
	      sourcesTitle: 'Contoh sumber data',
	      sources: ['CSV / XLSX', 'Database SQL', 'API', 'Data warehouse', 'Laporan PDF', 'Sistem internal'],
	      outputs: ['Dashboard', 'Insight', 'Peta'],
	    },
	    channels: {
	      title: 'Tanya dan akses — dari mana saja',
	      subtitle:
	        'Tidak perlu memaksa tim ganti cara kerja. Pakai ERICA via web dan integrasikan ke channel yang tim Anda sudah gunakan.',
	      bullets: [
	        'Tanya lewat WhatsApp atau Telegram (via integrasi), atau pakai web app.',
	        'Dapatkan jawaban ringkas + dashboard untuk drill‑down.',
	        'Bawa insight ke pengambil keputusan di tempat mereka bekerja.',
	      ],
	      methodsTitle: 'Opsi akses',
	      methods: ['Web app', 'Mobile friendly', 'WhatsApp (integrasi)', 'Telegram (integrasi)', 'Integrasi custom'],
	      note: 'Integrasi tersedia sesuai kebutuhan',
	      exampleTitle: 'Contoh',
	      exampleYou: 'Anda',
	      exampleQuestion: 'Tolong rangkum 3 isu utama percakapan minggu lalu dan siapkan dashboardnya.',
	      exampleAnswer:
	        'Berikut ringkasan isu utama, perubahan minggu ke minggu, dan prioritas tindak lanjut. Saya juga siapkan tampilan dashboard untuk drill‑down lebih dalam.',
	      exampleFooter: 'Link dashboard (internal) • Insight utama • Next steps',
	    },
	    modules: {
	      title: 'Satu platform. Banyak cara eksplorasi.',
	      subtitle:
	        'Dashboard, template, dokumen, dan peta—untuk pengambil keputusan.',
      screenshotAlt: 'Tangkapan layar platform ERICA',
      items: [
        {
          title: 'Dashboard otomatis sesuai kebutuhan',
          description:
            'Ubah pertanyaan menjadi chart, metrik, dan dashboard interaktif.',
        },
        {
          title: 'Template use case',
          description: 'Mulai dari template yang terbukti, lalu sesuaikan konteks.',
        },
        {
          title: 'Tanya dokumen Anda',
          description: 'Cari dan rangkum laporan/attachment dengan lebih percaya diri.',
        },
        {
          title: 'Insight berbasis lokasi',
          description: 'Pahami kondisi di peta, bukan hanya di tabel.',
        },
      ],
    },
    useCases: {
      title: 'Template use case untuk banyak bisnis',
      subtitle:
        'Mulai dari pengadaan, finance, HR, operasi, dan lainnya—beri data Anda ke ERICA dan dapatkan insight untuk aksi cepat.',
      highlights: [
        {
          title: 'Analisis data chatbot hotel',
          label: 'Hospitality',
          description:
            'Pahami intent tamu, isu teratas, dan outcome percakapan dari setiap sesi chat. ERICA menyorot pola masalah yang berulang dan peluang perbaikan layanan agar tim bisa menindaklanjuti lebih cepat.',
          tags: ['Chat analytics', 'Tren', 'Insight'],
          imageAlt: 'ERICA menganalisis data chatbot hotel',
        },
        {
          title: 'Insight data publik (kecelakaan)',
          label: 'Pemerintahan',
          description:
            'Ubah data publik seperti laporan kecelakaan menjadi dashboard yang mudah dipakai tim lapangan maupun pimpinan. ERICA membantu melihat hotspot, perubahan tren, dan sinyal prioritas untuk mendukung keputusan berbasis data.',
          tags: ['Data publik', 'Dashboard', 'Decision support'],
          imageAlt: 'Dashboard ERICA untuk data kecelakaan publik',
        },
        {
          title: 'Insight vendor & procurement',
          label: 'Pengadaan',
          description:
            'Eksplor performa vendor, sentimen survey, dan KPI pengadaan dalam satu alur kerja. ERICA menyatukan data tabel dan dokumen menjadi insight terstruktur yang memudahkan review serta tindak lanjut.',
          tags: ['Vendor', 'Survey', 'KPI'],
          imageAlt: 'ERICA menganalisis data vendor dan pengadaan',
        },
      ],
      moreTitle: 'Ide template lainnya',
      moreItems: [
        '1. Laporan Rencana Pengadaan Tahunan (Procurement Plan Report)',
        '4. Laporan Perencanaan/Realisasi Pengadaan (Procurement Plan vs Realisasi Pengadaan)',
        '11. Laporan Survey Kepuasan dari Vendor kepada Perseroan',
        '5. Laporan Keikutsertaan Vendor dalam Proses Pengadaan',
        '10. Laporan Direct Cost Saving',
        '2. Laporan Across Years Encumbrance',
        '9. Laporan SLA Pengadaan',
        '3. Laporan Summary FPBJ–SPK',
        '7. Laporan Statistik Pendaftaran Vendor',
        '6. Laporan Status Vendor (Blacklist, Verified, Watchlist)',
      ],
    },
    demoStory: {
      title: 'Intent → dashboard',
      subtitle:
        'Minta kebutuhan Anda. ERICA menghasilkan dashboard yang membantu pimpinan mengambil aksi cepat.',
      promptLabel: 'Contoh prompt',
      prompt: 'Tolong buatkan dashboard survey kepuasan vendor dari file terlampir.',
      screenshotAlt: 'Tangkapan layar alur chat dan dashboard',
    },
    security: {
      title: 'Keamanan & governance dulu',
      subtitle:
        'Dibangun agar selaras dengan governance enterprise, security review internal, dan kebutuhan compliance.',
      bullets: [
        'Didesain untuk mendukung keselarasan dengan UU PDP dan kebutuhan data governance',
        'Kami bisa bekerja bareng tim security/DPO Anda untuk kontrol akses, data handling, dan dokumen review',
        'Pendekatan deployment bisa disepakati sesuai constraint (cloud/on‑prem, sesuai kesepakatan)',
      ],
    },
    faq: {
      title: 'FAQ',
      subtitle: 'Jawaban singkat untuk tim yang mengevaluasi ERICA.',
      items: [
        {
          q: 'Data apa saja yang bisa diproses ERICA?',
          a: 'Format file dan sumber data bisnis yang umum. Di sesi demo, kita akan selaraskan dengan landscape data Anda.',
        },
        {
          q: 'Apakah ERICA mendukung Bahasa Indonesia?',
          a: 'Ya—ERICA mendukung Bahasa Indonesia dan English untuk workflow bisnis.',
        },
        {
          q: 'Berapa lama untuk mulai?',
          a: 'Bergantung pada data dan kebutuhan akses. Kita mulai dari demo, lalu susun jalur tercepat menuju value.',
        },
        {
          q: 'Apakah dashboard/template bisa dikustomisasi?',
          a: 'Ya—template dan dashboard bisa disesuaikan dengan KPI dan gaya reporting Anda.',
        },
        {
          q: 'Di mana data saya disimpan?',
          a: 'Opsi deployment dibahas saat security review agar sesuai kebijakan dan constraint Anda.',
        },
        {
          q: 'Bagaimana cara minta demo?',
          a: 'Isi form di bawah, dan kami akan menghubungi Anda untuk menjadwalkan sesi.',
        },
      ],
    },
    demo: {
      title: 'Minta demo',
      subtitle:
        'Ceritakan apa yang ingin Anda eksplorasi—dashboard, template, dokumen, atau peta.',
      contactTitle: 'Lebih nyaman via email?',
      contactBody: 'Kirim email ke sales@rekayasa.io dan kami akan membalas segera.',
      form: {
        fullName: 'Nama lengkap',
        workEmail: 'Email kantor',
        company: 'Perusahaan',
        role: 'Peran / Fungsi (opsional)',
        interest: 'Ingin eksplor apa?',
        message: 'Pesan (opsional)',
        consent: 'Saya setuju dihubungi terkait ERICA.',
        submit: 'Kirim permintaan',
        submitting: 'Mengirim…',
        successTitle: 'Permintaan diterima',
        successBody: 'Terima kasih—kami akan menghubungi Anda segera.',
        errorTitle: 'Terjadi kendala',
        errorBody:
          'Silakan coba lagi. Jika masih bermasalah, hubungi kami via email.',
        retry: 'Coba lagi',
        emailUs: 'Email kami',
        endpointMissingTitle: 'Form demo belum dikonfigurasi',
        endpointMissingBody:
          'Environment ini belum memiliki endpoint form. Silakan email kami untuk minta demo.',
        validation: {
          required: 'Wajib diisi.',
          invalidEmail: 'Masukkan email kantor yang valid.',
          consentRequired: 'Mohon centang persetujuan untuk dihubungi.',
        },
        interestOptions: {
          dashboard: 'Dashboard',
          templates: 'Template',
          documents: 'Document Assistant',
          map: 'Peta',
          notSure: 'Belum yakin',
        },
      },
    },
    cookie: {
      message:
        'Kami menggunakan cookies untuk meningkatkan pengalaman Anda. Dengan melanjutkan, Anda setuju dengan penggunaan cookies.',
      learnMore: 'Pelajari',
      accept: 'Setuju',
      close: 'Tutup',
    },
    footer: {
      tagline:
        'ERICA membantu tim bisnis mengubah data menjadi dashboard, insight, dan aksi.',
      emailLabel: 'Email',
      location: 'Jakarta, Indonesia',
      legal: 'Legal',
      company: 'Tentang Perusahaan',
      privacy: 'Kebijakan Privasi',
      terms: 'Syarat & Ketentuan',
      copyright: 'Rekayasa Analisa Digital. Hak cipta dilindungi.',
    },
  },
};
